import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEntitlement } from "@/context/EntitlementContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Crown, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Pricing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { active: isPro } = useEntitlement();
  const [loading, setLoading] = useState(false);

  const upgrade = async () => {
    if (!user) { nav("/register"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "aiapply_pro_monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFB]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#E2E8E4]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <Logo />
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <Button onClick={() => nav("/app")} variant="outline" className="rounded-full border-[#E2E8E4]" data-testid="pricing-open-app">Open app</Button>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="rounded-full">Sign in</Button></Link>
                <Link to="/register"><Button className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-16 text-center">
        <div className="overline">Pricing</div>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight mt-3">Simple. Honest. £4.99.</h1>
        <p className="text-[#5C6661] mt-4 max-w-xl mx-auto">Start free with 5 matched companies. Upgrade any time — one Pro subscription works across iPhone, Android and the web.</p>

        <div className="relative mt-12 mb-2 rounded-3xl overflow-hidden max-w-3xl mx-auto shadow-[0_20px_60px_-25px_rgba(45,74,62,0.35)]">
          <img
            src="https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400"
            alt="Fountain pen on notepaper"
            className="w-full h-56 md:h-72 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2D4A3E]/70 via-[#2D4A3E]/10 to-transparent" />
          <div className="absolute bottom-6 left-8 right-8 text-left text-white">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest bg-white/15 backdrop-blur rounded-full px-3 py-1"><Crown className="w-3 h-3" /> JobKnocker Pro</div>
            <div className="font-display text-2xl md:text-3xl font-bold mt-3">Every match. Every week. Every inbox.</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-10 text-left">
          <div className="rounded-xl border border-[#E2E8E4] bg-white p-8">
            <div className="overline">Free</div>
            <div className="font-display text-4xl font-bold mt-2">£0</div>
            <div className="text-xs text-[#5C6661] mt-1">Forever</div>
            <ul className="mt-6 space-y-3 text-sm text-[#121413]">
              {["Full CV parsing with Claude Sonnet 4.5","Up to 5 matched companies per search","AI-generated cover letters","Send from your own inbox","Sent history"].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2D4A3E] mt-0.5" />{f}</li>
              ))}
            </ul>
            {!user && <Link to="/register" data-testid="pricing-free-cta"><Button variant="outline" className="mt-8 w-full rounded-full border-[#E2E8E4]">Get started free</Button></Link>}
            {user && !isPro && <Button variant="outline" className="mt-8 w-full rounded-full border-[#E2E8E4]" disabled>Current plan</Button>}
          </div>
          <div className="rounded-xl border-2 border-[#2D4A3E] bg-[#E7F0EB]/30 p-8 relative">
            <div className="absolute -top-3 left-8 bg-[#2D4A3E] text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full">Recommended</div>
            <div className="overline text-[#2D4A3E]">JobKnocker Pro</div>
            <div className="font-display text-4xl font-bold mt-2">£4.99<span className="text-lg text-[#5C6661] font-normal"> / month</span></div>
            <div className="text-xs text-[#5C6661] mt-1">Cancel anytime</div>
            <ul className="mt-6 space-y-3 text-sm text-[#121413]">
              {["Everything in Free","Unlimited matched companies","Unlimited applications sent","Priority AI cover letters","One subscription works on iPhone, Android and web"].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2D4A3E] mt-0.5" />{f}</li>
              ))}
            </ul>
            {isPro ? (
              <Button className="mt-8 w-full rounded-full bg-[#2D4A3E]" disabled data-testid="pricing-current-pro"><Crown className="w-4 h-4 mr-2" /> Your current plan</Button>
            ) : (
              <Button onClick={upgrade} disabled={loading} className="mt-8 w-full rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" data-testid="pricing-upgrade-cta">
                {loading ? "Redirecting…" : (<>Start Pro <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-16 text-xs text-[#5C6661] pb-20">
          Payments are processed securely by Stripe. You can cancel at any time. VAT is calculated at checkout where applicable.
        </div>
      </section>

      <footer className="border-t border-[#E2E8E4] py-10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs text-[#5C6661]">
          <div>© {new Date().getFullYear()} JobKnocker</div>
          <div className="flex gap-6"><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></div>
        </div>
      </footer>
    </div>
  );
}
